import { type Consents, copy, type Goal, goalSchema } from '@qi/shared';
import { Button, FontSizeToggle, Switch, TextField, ThemeToggle } from '@qi/ui';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { authClient } from '../lib/auth-client.ts';
import { useMe, useUpdateProfile } from '../lib/queries.ts';
import { useSessionStore } from '../stores/session-store.ts';

export const Route = createFileRoute('/_app/settings')({
  beforeLoad: () => {
    const user = useSessionStore.getState().user;
    if (!user || user.email === '(guest)') {
      throw redirect({ to: '/auth', search: { mode: 'signin' } });
    }
  },
  component: SettingsPage,
});

const GOALS = goalSchema.options as readonly Goal[];

type MeData = NonNullable<ReturnType<typeof useMe>['data']>;

function SettingsPage() {
  const { data } = useMe();
  if (!data) {
    return <div className="mx-auto w-full max-w-2xl px-5 py-12 text-sm text-ink-3">Loading…</div>;
  }
  return <SettingsForm me={data} />;
}

function SettingsForm({ me }: { me: MeData }) {
  const setStoreGoal = useSessionStore((s) => s.setGoal);
  const updateProfile = useUpdateProfile();
  const navigate = useNavigate();

  const initialName = me.user?.name ?? '';
  const initialGoal = (me.profile?.goal ?? 'stress') as Goal;
  const initialConsents = me.profile?.consents ?? {
    analytics: true,
    improveQi: false,
    sessionReplay: false,
  };

  const [name, setName] = useState(initialName);
  const [goal, setGoal] = useState<Goal>(initialGoal);
  const [consents, setConsents] = useState<Consents>(initialConsents);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty =
    name.trim() !== initialName ||
    goal !== initialGoal ||
    consents.analytics !== initialConsents.analytics ||
    consents.improveQi !== initialConsents.improveQi ||
    consents.sessionReplay !== initialConsents.sessionReplay;

  async function onSave() {
    setError(null);
    setSaved(false);
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (trimmed !== initialName) {
        await authClient.updateUser({ name: trimmed });
      }
      await updateProfile.mutateAsync({ goal, consents });
      setStoreGoal(goal);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your changes');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-5 py-8 lg:py-12">
      <h1 className="font-mono text-xs uppercase tracking-[0.2em] text-ink-1">Settings</h1>

      <section aria-labelledby="settings-profile" className="mt-6 space-y-4">
        <SectionTitle id="settings-profile">Profile</SectionTitle>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label="Email" value={me.user?.email ?? ''} readOnly disabled />
      </section>

      <section aria-labelledby="settings-goal" className="mt-8 space-y-3">
        <SectionTitle id="settings-goal">Your goal</SectionTitle>
        <div className="flex flex-wrap gap-2">
          {GOALS.map((g) => {
            const active = g === goal;
            return (
              <button
                key={g}
                type="button"
                onClick={() => setGoal(g)}
                aria-pressed={active}
                className={`min-h-touch touch-manipulation rounded-md border px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora/60 ${
                  active
                    ? 'border-aurora/60 bg-[color-mix(in_oklab,var(--aurora)_12%,transparent)] text-ink-0'
                    : 'border-hairline text-ink-2 hover:text-ink-0'
                }`}
              >
                {copy.goalCopy[g].title}
              </button>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="settings-privacy" className="mt-8 space-y-3">
        <SectionTitle id="settings-privacy">Privacy &amp; data</SectionTitle>
        <ConsentRow
          label="Analytics"
          checked={consents.analytics}
          onChange={(v) => setConsents((c) => ({ ...c, analytics: v }))}
        />
        <ConsentRow
          label="Improve Qi"
          checked={consents.improveQi}
          onChange={(v) => setConsents((c) => ({ ...c, improveQi: v }))}
        />
        <ConsentRow
          label="Session replay"
          checked={consents.sessionReplay}
          onChange={(v) => setConsents((c) => ({ ...c, sessionReplay: v }))}
        />
        <Link to="/privacy" className="inline-block text-xs text-aurora hover:underline">
          Read our privacy policy →
        </Link>
      </section>

      <section aria-labelledby="settings-appearance" className="mt-8 space-y-3">
        <SectionTitle id="settings-appearance">Appearance</SectionTitle>
        <div className="flex items-center justify-between">
          <span className="text-sm text-ink-1">Theme</span>
          <ThemeToggle />
        </div>
        <div className="space-y-2">
          <span className="block text-sm text-ink-1">Text size</span>
          <FontSizeToggle onSelect={(fontScale) => updateProfile.mutate({ fontScale })} />
          <p className="text-xs text-ink-3">Applies everywhere. Saved to your account.</p>
        </div>
      </section>

      <section aria-labelledby="settings-account" className="mt-8 space-y-3">
        <SectionTitle id="settings-account">Account</SectionTitle>
        <p className="text-sm text-ink-2">
          Plan:{' '}
          <span className="text-ink-0">{me.user?.plan === 'premium' ? 'Premium' : 'Free'}</span>
        </p>
        <Button
          variant="ghost"
          onClick={async () => {
            await authClient.signOut();
            useSessionStore.getState().signOut();
            navigate({ to: '/' });
          }}
        >
          Sign out
        </Button>
      </section>

      <div className="mt-10 flex items-center gap-3 border-t border-hairline pt-5">
        <Button variant="resonance" onClick={onSave} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        {saved && !dirty && <span className="text-xs text-jade">Saved</span>}
        {error && (
          <span role="alert" className="text-xs text-rose">
            {error}
          </span>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2 id={id} className="font-mono text-xs uppercase tracking-[0.18em] text-ink-3">
      {children}
    </h2>
  );
}

function ConsentRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-ink-1">{label}</span>
      <Switch label={label} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
